import { uniq } from "lodash";
import { FlexContainerType, FlexEntityUploadReport } from "../../../../models/generated/graphql";

interface GetReportFileDataProps {
	report: FlexEntityUploadReport | undefined | null;
	userOrgName: string | null;
	formType: string;
	t: (str: string, options?: any) => string;
	isPreliminary: boolean;
	uploadType: FlexContainerType;
	isBulk?: boolean | null;
}

interface RecordCoreValues {
	key: string;
	patientId: string;
	productId: string;
	administrationDate: string;
}

const formatEntry = (entry: { patientId: string; rowNumber?: number | null }): string =>
	entry.rowNumber != null ? `${entry.rowNumber}_${entry.patientId}` : entry.patientId;

const getReportFileData = (props: GetReportFileDataProps) => {
	const { report, userOrgName, formType, t, isPreliminary, uploadType, isBulk } = props;
	const preliminaryReportText = isPreliminary ? "pre-" : "";
	const isFormUpload = uploadType === FlexContainerType.Form;
	const uploadTypeAsString = `${uploadType.toLowerCase()}s`;
	const processedRecords = report?.percentageSourceFile || 0;

	const submittedEntities = report?.submittedEntities || [];
	const distinctSubmittedPatientCount = new Set(submittedEntities.map((e) => e.patientId)).size;
	const updatedAmount = new Set(report?.updatedEntities?.filter((entity) => !entity.isSubmitted).map((e) => e.patientId) || []).size;
	const updatedAndSubmittedAmount = new Set(report?.updatedEntities?.filter((entity) => entity.isSubmitted).map((e) => e.patientId) || [])
		.size;
	const draftAmount = new Set(report?.draftedEntities?.map((e) => e.patientId)).size;
	const failedToCreateAmount = new Set(report?.failedToCreate.filter((e) => !e.hasImpossibleHighQuantity).map((e) => e.patientId) || [])
		.size;
	const failedToCreateDueToQuantity = report?.failedToCreate.filter((e) => e.hasImpossibleHighQuantity) ?? [];
	const failedToUpdateAmount = new Set(report?.failedToUpdate?.map((e) => e.patientId) || []).size;
	const failedToAddDuplicate = report?.failedToAdd ? (JSON.parse(report.failedToAdd) as Array<RecordCoreValues>) : [];
	const ignoredRecords = report?.ignoredRecords ? (JSON.parse(report.ignoredRecords) as Array<RecordCoreValues>) : [];
	const isPatientOrPurchase = report?.isCaselessRecord ? t("product") : t("patient");

	const updatedFormsText = getText(
		`
- ${t(`${preliminaryReportText}upload-forms-updated`, {
			amount: updatedAmount,
		})};
${report?.updatedEntities
	?.filter((entity) => !entity.isSubmitted)
	.map((form) => `${formatEntry(form)} (${form.attributesUpdated} attributes added);`)
	.join("\n")}`,
		!!updatedAmount
	);

	const updatedAndSubmittedFormsText = getText(
		`
- ${t(`${preliminaryReportText}upload-forms-updated-and-submitted`, {
			amount: updatedAndSubmittedAmount,
		})};
${report?.updatedEntities
	?.filter((entity) => entity.isSubmitted)
	.map((form) => `${formatEntry(form)} (${form.attributesUpdated} attributes added);`)
	.join("\n")}`,
		!!updatedAndSubmittedAmount
	);

	const draftedFormsText = getText(
		`
- ${t(`${preliminaryReportText}upload-forms-drafted`, { amount: draftAmount })};
	${report?.draftedEntities
		?.map(
			(form) =>
				`${formatEntry(form)} (attributes ${form.invalidAttributes.map((attribute) => `"${attribute.name}"`).join(", ")} missing);`
		)
		.join(",\n")}`,
		!!draftAmount
	);

	const failedToCreateText = getText(
		`
- ${t(`${preliminaryReportText}upload-${uploadTypeAsString}-creation-failed`, { amount: failedToCreateAmount, isPatientOrPurchase })};
	${report?.failedToCreate
		.map((form) => {
			if (form.hasNoValidDate) {
				return `${formatEntry(form)} ${t("no-valid-date")}`;
			}

			if (form.hasImpossibleHighQuantity) {
				return null;
			}

			return `${formatEntry(form)} ${
				form.hasNoValidScope
					? t(`no-valid-scope`)
					: `(attributes ${form.invalidAttributes.map((attribute) => `"${attribute.name}"`).join(", ")} invalid);`
			}`;
		})
		.join("\n")}`,
		!!failedToCreateAmount
	);

	const failedToCreateDueToQuantityText = getText(
		`
- ${t("pre-record-upload-error-due-to-quantity", { amount: failedToCreateDueToQuantity.length })}
\t${uniq(failedToCreateDueToQuantity?.map((e) => e.quantity) ?? [])
			.sort()
			.join(", ")}
		`,
		!!failedToCreateDueToQuantity?.length
	);

	const failedToUpdateText = getText(
		`- ${t(`${preliminaryReportText}upload-forms-update-failed`, { amount: report?.failedToUpdate?.length })};
	${report?.failedToUpdate?.map(formatEntry).join(",\n")}`,
		!!failedToUpdateAmount
	);

	const duplicatesFromFileText = getText(
		`
- ${t(`${preliminaryReportText}existing-records-count`, { amount: failedToAddDuplicate.length })};
    ${failedToAddDuplicate.map((ignoredRecord) => Object.values(ignoredRecord).filter(Boolean).join(", ")).join(",\n")}`,
		!!failedToAddDuplicate.length
	);

	const ignoredFromFileText = getText(
		`
- ${t(`${preliminaryReportText}upload-${uploadTypeAsString}-duplicated`, { amount: ignoredRecords.length })};
	${ignoredRecords.map((ignoredRecord) => Object.values(ignoredRecord).filter(Boolean).join(", ")).join(",\n")}`,
		!!ignoredRecords.length
	);

	const revokedRecordsText = getText(
		`
- ${t(`${preliminaryReportText}upload-${uploadTypeAsString}-revoked`, {
			amount: report?.revokedEntities?.length,
			patientsCount: new Set(report?.revokedEntities?.map((e) => e.patientId) || []).size,
			isPatientOrPurchase,
		})};
${(report?.revokedEntities?.map(formatEntry) || []).join(",\n")}
`,
		!!report?.revokedEntities?.length
	);

	const failToRevokedRecordsText = getText(
		`
- ${t(`${preliminaryReportText}upload-${uploadTypeAsString}-revocation-failed`, {
			amount: report?.failedToRevoke?.length,
			patientsCount: new Set(report?.failedToRevoke?.map((e) => e.patientId) || []).size,
			isPatientOrPurchase,
		})};
${(report?.failedToRevoke?.map(formatEntry) || []).join(",\n")}
`,
		!!report?.failedToRevoke?.length
	);

	const formUploadInformation = `${
		updatedFormsText + updatedAndSubmittedFormsText + draftedFormsText + failedToCreateText + failedToUpdateText
	}`;

	const recordsProcessed = getText(
		`
- ${t(`percent-source-file`, {
			amount: processedRecords,
			recordCount: report?.submittedEntities?.length,
		})};
	`,
		!!processedRecords && !isPreliminary
	);

	const recordUploadStats = [
		revokedRecordsText,
		failToRevokedRecordsText,
		failedToCreateText,
		failedToCreateDueToQuantityText,
		recordsProcessed,
		report?.failedToUpdate?.map(formatEntry).join(",\n"),
		failedToUpdateText,
		duplicatesFromFileText,
		ignoredFromFileText,
	]
		.filter(Boolean)
		.join("\n");

	const caseInformation = getText(
		`
- ${t(`${preliminaryReportText}upload-${uploadTypeAsString}-cases-created`, {
			amount: report?.patientsCreated.length,
			noCases: report?.casesCreated.length,
			isPatientOrPurchase,
		})};
	${report?.casesCreated.join(",\n")}
	`,
		!!report?.casesCreated.length
	);

	const submittedEntitiesCountText = getText(
		`
- ${t(`${preliminaryReportText}upload-${uploadTypeAsString}-submitted`, { amount: distinctSubmittedPatientCount, isPatientOrPurchase })};
	`,
		!!submittedEntities.length
	);

	const content = [
		submittedEntitiesCountText,
		submittedEntities.map(formatEntry).join(",\n"),
		getText(formUploadInformation, isFormUpload),
		caseInformation,
		getText(recordUploadStats, !isFormUpload),
	]
		.filter(Boolean)
		.join("\n");

	return `
${t(isBulk ? "bulk-coverage-report-details" : "upload-report-details")}:

${t("upload-report-generated-on")}: ${new Date().toISOString()}
${t("upload-report-generated-for")}: ${userOrgName}
${t("upload-report-form-type")}: ${formType}
${t("upload-report-completed-in")}: ${new Date(report?.completedIn || 0).getMinutes()} ${t("minutes")} ${new Date(
		report?.completedIn || 0
	).getSeconds()} ${t("seconds")}

${t(isBulk ? "bulk-coverage-report-summary" : "upload-report-summary")}:
${content}`;
};

const getText = (value: string, shouldBeDisplayed: boolean) => {
	return shouldBeDisplayed ? value : "";
};
export default getReportFileData;
